<template>
  <div class="tabulation-sheet-form">
    <!-- Loading Overlay -->
    <b-overlay :show="dataLoading || submitting" rounded="sm" class="w-100">
      <template #overlay>
        <div class="d-flex justify-content-center mb-3 mt-5">
          <b-spinner
            style="width: 3rem; height: 3rem"
            type="grow"
            variant="primary"
            label="Spinning"
          ></b-spinner>
        </div>
      </template>

      <b-row class="mb-2">
        <b-col md="12" class="d-flex align-items-center">
          <b-button
            variant="outline-secondary"
            @click="goBack"
            class="btn-icon mr-2 rounded-circle"
          >
            <feather-icon class="" icon="ArrowLeftIcon" />
          </b-button>
          <h4 class="mb-0">
            <template v-if="isHpgsDomain">Qualitative Review</template>
            <template v-else>
              {{ isView ? "View" : isEdit ? "Edit" : "Add" }} Tabulation Sheet
            </template>
          </h4>
        </b-col>
      </b-row>

      <!-- View Mode - Use Separate Component -->
      <TabulationSheetView
        v-if="isView && selectedType && currentTypeConfig"
        :class-info="getClassDisplayName()"
        :section-info="getSectionDisplayName()"
        :subject-info="getSubjectName(selectedSubject)"
        :exam-type-info="getExamTypeName(selectedExamType)"
        :session-info="getSessionName(selectedSession)"
        :type="selectedType"
        :type-display-name="currentTypeConfig.displayName"
        :categories="selectedType === 'icon-base' ? rows : []"
        :records="selectedType !== 'icon-base' ? rows : []"
        :loading="dataLoading"
        :sections-mode="isMadinatulIlmSectionsMode"
      />

      <!-- Skeleton Loading for View Mode -->
      <div v-if="isView && dataLoading" class="view-skeleton-container">
        <b-card class="view-header-card-skeleton">
          <div class="skeleton-header">
            <div class="skeleton-title"></div>
            <div class="skeleton-badge"></div>
          </div>
          <div class="skeleton-meta">
            <div v-for="n in 6" :key="n" class="skeleton-meta-item"></div>
          </div>
        </b-card>
        <b-card class="view-content-skeleton">
          <div v-for="n in 3" :key="n" class="skeleton-content-row">
            <div class="skeleton-content-item"></div>
            <div class="skeleton-content-item"></div>
            <div class="skeleton-content-item"></div>
          </div>
        </b-card>
      </div>

      <!-- Edit/Add Mode -->
      <b-card v-if="!isView">
        <!-- Class, Section, Subject Selectors -->
        <b-row class="mb-2">
          <b-col md="4">
            <b-form-group
              label="Class"
              :invalid-feedback="validationErrors.class"
            >
              <v-select
                v-model="selectedClassIds"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="classes"
                :reduce="(val) => String(val.id)"
                label="name"
                multiple
                :close-on-select="false"
                placeholder="Select classes"
                @input="onClassChange"
              />
              <small v-if="isMultiClassSelection" class="text-muted">
                Multiple classes selected — section is set to All.
              </small>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group
              label="Section"
              :invalid-feedback="validationErrors.section"
            >
              <v-select
                v-model="selectedSection"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sections"
                :reduce="(val) => val.id"
                label="section"
                :clearable="false"
                :disabled="!hasClassSelection || isMultiClassSelection"
                placeholder="Select Section"
                @input="onSectionChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group
              label="Subject"
              :invalid-feedback="validationErrors.subject"
            >
              <v-select
                v-model="selectedSubject"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="subjects"
                :reduce="(val) => val.id"
                label="subject"
                :clearable="false"
                :disabled="!hasClassSelection"
                placeholder="Select Subject"
                @input="onSubjectChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col md="4">
            <b-form-group
              label="Exam Type"
              :invalid-feedback="validationErrors.examType"
            >
              <v-select
                v-model="selectedExamType"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="examTypes"
                :reduce="(val) => val.id"
                label="examType"
                :clearable="false"
                placeholder="Select Exam Type"
                @input="onExamTypeChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group
              label="Session"
              :invalid-feedback="validationErrors.session"
            >
              <v-select
                v-model="selectedSession"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sessions"
                :reduce="(val) => val.id"
                label="session"
                :clearable="false"
                placeholder="Select Session"
                @input="onSessionChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" v-if="isEdit && !isHpgsDomain">
            <b-form-group label="Due Date">
              <flat-pickr
                v-model="dueDate"
                :config="dueDateConfig"
                class="form-control"
                placeholder="Select due date"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <hr class="my-2" />

        <!-- Type Selector (hidden on remarks-base schools add — defaults to Remarks Base) -->
        <b-row v-if="showTypeSelector" class="mb-2">
          <b-col md="12">
            <b-form-group
              label="Type"
              :invalid-feedback="validationErrors.type"
            >
              <div class="type-selector">
                <div
                  v-for="type in typeOptions"
                  :key="type.typeName"
                  class="type-item"
                  :class="{
                    active: selectedType === type.typeName,
                    disabled:
                      isEdit && loadedType && loadedType !== type.typeName,
                  }"
                  :style="{
                    cursor:
                      isEdit && loadedType && loadedType !== type.typeName
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      isEdit && loadedType && loadedType !== type.typeName
                        ? 0.5
                        : 1,
                  }"
                  @click="
                    isEdit && loadedType && loadedType !== type.typeName
                      ? null
                      : selectType(type.typeName)
                  "
                >
                  <div class="type-icon-wrapper">
                    <feather-icon
                      v-if="selectedType === type.typeName"
                      icon="XIcon"
                      size="20"
                      class="type-icon"
                    />
                    <feather-icon
                      v-else
                      icon="PlusIcon"
                      size="20"
                      class="type-icon"
                    />
                  </div>
                  <span class="type-label">{{ type.displayName }}</span>
                  <span
                    v-if="isEdit && loadedType && loadedType !== type.typeName"
                    class="type-disabled-badge"
                  >
                  </span>
                </div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Edit/Add Mode - Form Fields -->
        <div
          v-if="selectedType && currentTypeConfig"
          class="form-content-section"
        >
          <div
            class="form-section-header"
            :class="{ 'icon-base-header': isIconBase }"
          >
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="section-title">{{ currentTypeConfig.displayName }}</h5>
              <!-- Add Category Button for Icon Base -->
              <b-button
                v-if="isIconBase"
                @click="addCategory"
                class="add-category-header-btn"
              >
                <feather-icon icon="PlusIcon" size="16" />
              </b-button>
              <!-- Add Row Button (only for multiple types, but not icon-base) -->
              <b-button
                v-else-if="currentTypeConfig.multiple && !isQuestionsBase"
                @click="addRow"
                class="add-row-btn"
              >
                <feather-icon icon="PlusIcon" size="16" />
              </b-button>
            </div>
          </div>

          <div
            class="form-rows-container"
            :class="{
              'icon-base-container': isIconBase,
              'question-base-container': isQuestionsBase,
            }"
          >
            <template v-if="isIconBase">
              <div class="icon-base-categories">
                <!-- Category Cards -->
                <div
                  v-for="(category, categoryIndex) in rows"
                  :key="category.__rowId || categoryIndex"
                  class="category-card"
                  :class="{ 'profectus-prejr-card': showCategoryLanguageToggle }"
                >
                  <div
                    class="category-header"
                    :class="{ 'category-header--prejr': showCategoryLanguageToggle }"
                  >
                    <b-form-input
                      v-model="category.categoryName"
                      :placeholder="
                        profectusUsesSubjectAutoFill
                          ? 'Select the subject to auto fill this category name'
                          : 'Enter category name (e.g., Soft skills)'
                      "
                      class="category-name-input"
                      :class="{
                        'category-name-input--auto': profectusCategoryInputDisabled,
                      }"
                      :disabled="isView || profectusCategoryInputDisabled"
                      :readonly="isView || profectusCategoryInputDisabled"
                    />
                    <b-button
                      v-if="showCategoryLanguageToggle && !isView"
                      variant="outline-primary"
                      size="sm"
                      class="profectus-lang-expand-btn"
                      @click="toggleCategoryLanguagePanel(categoryIndex)"
                    >
                      <feather-icon
                        :icon="
                          category.languagePanelOpen
                            ? 'ChevronUpIcon'
                            : 'ChevronDownIcon'
                        "
                        size="16"
                      />
                    </b-button>
                    <div
                      v-if="
                        showCategoryLanguageToggle &&
                        (category.languagePanelOpen || isView)
                      "
                      class="profectus-lang-inline-group"
                    >
                      <span
                        v-for="lang in profectusLanguageOptions"
                        :key="lang.code"
                        class="profectus-lang-box active is-display-only"
                      >
                        <span class="profectus-lang-box-code">{{ lang.code }}</span>
                        <span class="profectus-lang-box-label">{{
                          lang.shortLabel
                        }}</span>
                      </span>
                    </div>
                    <b-button
                      v-if="!isView"
                      variant="outline-danger"
                      size="sm"
                      class="remove-category-btn"
                      @click="removeRow(categoryIndex)"
                    >
                      <feather-icon icon="XIcon" size="14" />
                    </b-button>
                  </div>

                  <div class="category-options">
                    <div class="options-header">
                      <span class="options-label">Options:</span>
                      <b-button
                        v-if="!isView"
                        @click="addOption(categoryIndex)"
                        class="add-option-btn"
                        variant="primary"
                        size="sm"
                      >
                        <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                        Add Option
                      </b-button>
                    </div>

                    <draggable
                      v-model="category.options"
                      handle=".option-drag-handle"
                      class="options-draggable"
                      :animation="200"
                      :disabled="isView"
                    >
                      <div
                        v-for="(option, optionIndex) in category.options"
                        :key="option.__optionId || optionIndex"
                        class="option-item"
                      >
                        <span
                          class="option-drag-handle"
                          title="Drag to reorder"
                        >
                          <span class="handle-dot"></span>
                          <span class="handle-dot"></span>
                          <span class="handle-dot"></span>
                        </span>
                        <b-form-input
                          v-model="option.optionName"
                          placeholder="Enter option name"
                          class="option-input"
                          :disabled="isView"
                          :readonly="isView"
                        />
                        <b-button
                          v-if="!isView"
                          variant="outline-danger"
                          size="sm"
                          class="remove-option-btn"
                          @click="removeOption(categoryIndex, optionIndex)"
                        >
                          <feather-icon icon="XIcon" size="14" />
                        </b-button>
                      </div>
                    </draggable>

                    <div
                      v-if="!category.options || category.options.length === 0"
                      class="no-options-message"
                    >
                      No options added. Click "Add Option" to add options to
                      this category.
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="rows.length === 0"
                class="text-muted text-center py-3 small"
              >
                Click the plus button above to add your first category.
              </div>
            </template>

            <template v-else-if="isQuestionsBase">
              <div class="question-config-panel">
                <div class="question-count-block">
                  <label class="question-count-label">{{
                    questionsCountLabel
                  }}</label>
                  <div class="question-count-input">
                    <b-form-input
                      v-model.number="questionCountInput"
                      type="number"
                      min="1"
                      :placeholder="
                        isMadinatulIlmSectionsMode ? 'e.g. 3' : 'e.g. 5'
                      "
                      :disabled="isView"
                      :readonly="isView"
                      @keyup.enter="!isView && applyQuestionCount"
                    />
                    <b-button
                      v-if="!isView"
                      variant="primary"
                      class="ml-50"
                      @click="applyQuestionCount"
                    >
                      Apply
                    </b-button>
                  </div>
                  <small class="text-muted">{{ questionsCountHint }}</small>
                </div>
                <div class="question-total-card">
                  <span class="label">Total Marks</span>
                  <span class="value">{{ questionsTotalMarks }}</span>
                </div>
              </div>

              <draggable
                v-model="rows"
                handle=".question-drag-handle"
                class="question-draggable"
                :animation="200"
                :disabled="isView"
              >
                <div
                  v-for="(row, rowIndex) in rows"
                  :key="row.__rowId || rowIndex"
                  class="question-row-card"
                >
                  <div class="question-row-header">
                    <span class="question-drag-handle" title="Drag to reorder">
                      <span class="handle-dot"></span>
                      <span class="handle-dot"></span>
                      <span class="handle-dot"></span>
                    </span>
                    <span class="question-row-title">{{
                      getQuestionRowTitle(row, rowIndex)
                    }}</span>
                    <div class="ml-auto d-flex align-items-center">
                      <span class="question-row-marks mr-1">
                        Marks: {{ Number(row.totalMarks) || 0 }}
                      </span>
                      <b-button
                        v-if="!isView"
                        variant="outline-danger"
                        size="sm"
                        class="remove-row-btn"
                        @click="removeRow(rowIndex)"
                      >
                        <feather-icon icon="XIcon" size="14" />
                      </b-button>
                    </div>
                  </div>
                  <b-row class="form-fields-row">
                    <b-col
                      v-for="category in currentTypeConfig.categories"
                      :key="category.key"
                      :md="6"
                      class="form-field-col"
                    >
                      <b-form-group :label="getQuestionCategoryLabel(category)">
                        <b-form-input
                          v-if="category.input === 'text'"
                          v-model="row[category.key]"
                          :placeholder="
                            getQuestionCategoryPlaceholder(category)
                          "
                          :disabled="isView"
                          :readonly="isView"
                        />
                        <b-form-input
                          v-else-if="category.input === 'number'"
                          v-model.number="row[category.key]"
                          type="number"
                          :placeholder="
                            getQuestionCategoryPlaceholder(category)
                          "
                          :disabled="isView"
                          :readonly="isView"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </draggable>
            </template>

            <template v-else-if="isSingleType">
              <!-- Single Type Form -->
              <div class="single-type-form">
                <b-row class="form-fields-row">
                  <b-col
                    v-for="category in currentTypeConfig.categories"
                    :key="category.key"
                    :md="
                      category.input === 'text' && category.key === 'remarks'
                        ? 12
                        : 4
                    "
                    class="form-field-col"
                  >
                    <b-form-group :label="category.name">
                      <!-- Text Input -->
                      <b-form-input
                        v-if="category.input === 'text'"
                        v-model="rows[0][category.key]"
                        :placeholder="`Enter ${category.name}`"
                        size="sm"
                        :disabled="isView"
                        :readonly="isView"
                      />

                      <!-- Number Input -->
                      <b-form-input
                        v-else-if="category.input === 'number'"
                        v-model.number="rows[0][category.key]"
                        type="number"
                        :placeholder="`Enter ${category.name}`"
                        size="sm"
                        :disabled="isView"
                        :readonly="isView"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </template>

            <template v-else>
              <!-- Dynamic Rows (for other multiple types) -->
              <div
                v-for="(row, rowIndex) in rows"
                :key="row.__rowId || rowIndex"
                class="form-row-card"
              >
                <div class="row-header" v-if="currentTypeConfig.multiple">
                  <span class="row-label">
                    <span
                      v-if="
                        selectedType === 'questions-base' &&
                        row.questionNumber &&
                        row.totalMarks
                      "
                    >
                      Q{{ row.questionNumber }} total marks {{ row.totalMarks }}
                    </span>
                    <span v-else> Row {{ rowIndex + 1 }} </span>
                  </span>
                  <b-button
                    v-if="!isView"
                    variant="outline-danger"
                    size="sm"
                    @click="removeRow(rowIndex)"
                    class="remove-row-btn"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </b-button>
                </div>

                <b-row class="form-fields-row">
                  <b-col
                    v-for="category in currentTypeConfig.categories"
                    :key="category.key"
                    :md="getColumnSize(category.input)"
                    class="form-field-col"
                  >
                    <b-form-group :label="category.name">
                      <!-- Text Input -->
                      <b-form-input
                        v-if="category.input === 'text'"
                        v-model="row[category.key]"
                        :placeholder="`Enter ${category.name}`"
                        :disabled="isView"
                        :readonly="isView"
                      />

                      <!-- Number Input -->
                      <b-form-input
                        v-else-if="category.input === 'number'"
                        v-model.number="row[category.key]"
                        type="number"
                        :placeholder="`Enter ${category.name}`"
                        :disabled="isView"
                        :readonly="isView"
                      />

                      <!-- Icon Toggle (Boolean) with User Input Name -->
                      <div
                        v-else-if="category.input === 'icon'"
                        class="icon-input-wrapper"
                      >
                        <b-form-input
                          v-model="row[category.key + '_name']"
                          :placeholder="`Enter ${category.name} name`"
                          class="icon-name-input"
                          :disabled="isView"
                          :readonly="isView"
                        />
                        <b-form-checkbox
                          v-model="row[category.key]"
                          switch
                          class="icon-toggle"
                          :disabled="isView"
                        >
                          <span
                            v-if="row[category.key]"
                            class="toggle-label-active"
                          >
                            {{ row[category.key + "_name"] || category.name }}
                          </span>
                          <span v-else class="toggle-label-inactive">
                            {{ row[category.key + "_name"] || category.name }}
                          </span>
                        </b-form-checkbox>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </template>
          </div>
        </div>

        <!-- Submit Button - Only in Edit/Add Mode -->
        <div class="d-flex justify-content-end mt-3 mb-2" v-if="!isView">
          <b-button
            variant="outline-secondary"
            @click="goBack"
            class="mr-2 cancel-btn"
            size="md"
          >
            <feather-icon icon="XIcon" size="16" class="mr-50" />
            Cancel
          </b-button>
          <b-button
            variant="success"
            @click="handleSubmit"
            :disabled="!canSubmit || submitting"
            class="save-btn"
            size="md"
          >
            <b-spinner v-if="submitting" small class="mr-50" />
            <feather-icon v-else icon="CheckIcon" size="16" class="mr-50" />
            {{ submitting ? "Submitting..." : isEdit ? "Update" : "Save" }}
          </b-button>
        </div>
        <!-- View Mode - Only Back Button -->
        <div class="d-flex justify-content-end mt-3 mb-2" v-else>
          <b-button
            variant="primary"
            @click="goBack"
            class="cancel-btn"
            size="md"
          >
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
            Back
          </b-button>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BButton,
  BSpinner,
  BOverlay,
} from "bootstrap-vue";
import vSelect from "vue-select";
import draggable from "vuedraggable";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import TabulationSheetView from "./TabulationSheetView.vue";

const PROFECTUS_LANGUAGE_OPTIONS = [
  {
    code: "E",
    key: "english",
    label: "English (E)",
    shortLabel: "English",
    categoryName: "ENGLISH",
  },
  {
    code: "U",
    key: "urdu",
    label: "Urdu (U)",
    shortLabel: "Urdu",
    categoryName: "URDU",
  },
];

// Dataset structure
const dataset = {
  types: [
    {
      typeName: "icon-base",
      displayName: "Icon Base",
      categories: [],
      multiple: true,
    },
    {
      typeName: "questions-base",
      displayName: "Questions Base",
      categories: [
        { name: "Question Number", key: "questionNumber", input: "text" },
        { name: "Total Marks", key: "totalMarks", input: "number" },
      ],
      multiple: true,
    },
    {
      typeName: "class-test",
      displayName: "Class Test",
      categories: [
        { name: "Name", key: "name", input: "text" },
        { name: "Total Question", key: "totalQuestion", input: "number" },
        { name: "Total Marks", key: "totalMarks", input: "number" },
      ],
      multiple: true,
    },
  ],
};

export default {
  name: "TabulationSheetForm",
  components: {
    TabulationSheetView,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BButton,
    BSpinner,
    BOverlay,
    vSelect,
    draggable,
    flatPickr,
  },
  data() {
    return {
      dataset,
      isEdit: false,
      isView: false,
      dataLoading: false,
      selectedClassIds: [],
      selectedSection: 0,
      selectedSubject: null,
      selectedType: null,
      rows: [],
      questionCountInput: 1,
      rowUidCounter: 0,
      submitting: false,
      rights: {},
      validationErrors: {
        class: "",
        section: "",
        subject: "",
        examType: "",
        session: "",
        type: "",
      },
      classes: [],
      classesData: [], // Store full class data with sections
      sections: [{ id: 0, section: "All", name: "All" }],
      subjects: [],
      examTypes: [],
      selectedExamType: null,
      sessions: [],
      selectedSession: null,
      loadedType: null, // Track the type of loaded data to disable other options in edit mode
      dueDate: null, // Due date for the tabulation sheet
    };
  },
  computed: {
    profectusLanguageOptions() {
      return PROFECTUS_LANGUAGE_OPTIONS;
    },
    hasClassSelection() {
      return (
        Array.isArray(this.selectedClassIds) &&
        this.selectedClassIds.length > 0
      );
    },
    isMultiClassSelection() {
      return (
        Array.isArray(this.selectedClassIds) &&
        this.selectedClassIds.length > 1
      );
    },
    primaryClassId() {
      if (!this.hasClassSelection || this.isMultiClassSelection) {
        return null;
      }
      return this.selectedClassIds[0];
    },
    selectedClassName() {
      return this.getClassName(this.primaryClassId) || "";
    },
    isProfectusPreJuniorClass() {
      return (
        this.isProfectusDomain &&
        this.isProfectusPreJuniorClassName(this.selectedClassName)
      );
    },
    isProfectusJuniorAutoFill() {
      return (
        this.isProfectusDomain &&
        this.isProfectusJuniorClassName(this.selectedClassName)
      );
    },
    profectusUsesSubjectAutoFill() {
      return this.isProfectusJuniorAutoFill;
    },
    profectusCategoryInputDisabled() {
      return this.profectusUsesSubjectAutoFill;
    },
    reportDomain() {
      return String(
        this.$store.state.report || this.$store.state.clientID || ""
      )
        .replace(/_/g, "")
        .toLowerCase()
        .trim();
    },
    isProfectusDomain() {
      return (
        this.reportDomain === "myskoolprofectus" ||
        this.reportDomain.includes("profectus")
      );
    },
    isMyskoolDomain() {
      return this.reportDomain === "myskool";
    },
    isHpgsDomain() {
      return (
        this.reportDomain === "myskoolhpgs" ||
        this.reportDomain === "hpgs" ||
        this.reportDomain.includes("hpgs")
      );
    },
    isWhssDomain() {
      return (
        this.reportDomain === "myskoolwhss" ||
        this.reportDomain === "whss" ||
        this.reportDomain.includes("whss")
      );
    },
    hidesTabulationTypePicker() {
      return this.isHpgsDomain || this.isWhssDomain;
    },
    isMadinatulIlmDomain() {
      return (
        this.reportDomain === "myskoolmadinatulilm" ||
        this.reportDomain.includes("madinatulilm")
      );
    },
    isMadinatulIlmSectionsMode() {
      return this.isMadinatulIlmDomain && this.isQuestionsBase;
    },
    isRemarksBaseSchool() {
      return (
        this.isProfectusDomain || this.isMyskoolDomain || this.isHpgsDomain
      );
    },
    showCategoryLanguageToggle() {
      if (!this.isIconBase) return false;
      if (this.isMyskoolDomain) return true;
      if (!this.isProfectusDomain) return false;
      // For Profectus, keep toggle for pre-junior and also allow in edit mode
      // so existing sheets can manage EN/UR remarks.
      return this.isProfectusPreJuniorClass || this.isEdit;
    },
    usesBothRemarksPayload() {
      return this.showCategoryLanguageToggle;
    },
    typeOptions() {
      if (this.isMadinatulIlmDomain) {
        const questionsType = this.dataset.types.find(
          (t) => t.typeName === "questions-base"
        );
        return questionsType
          ? [{ ...questionsType, displayName: "Sections" }]
          : [];
      }
      if (this.isRemarksBaseSchool) {
        const iconBaseType = this.dataset.types.find(
          (t) => t.typeName === "icon-base"
        );
        const remarksBaseType = iconBaseType
          ? { ...iconBaseType, displayName: "Remarks Base" }
          : null;
        if (
          this.isEdit &&
          this.loadedType &&
          this.loadedType !== "icon-base"
        ) {
          const loadedType = this.dataset.types.find(
            (t) => t.typeName === this.loadedType
          );
          return loadedType ? [loadedType] : remarksBaseType ? [remarksBaseType] : [];
        }
        return remarksBaseType ? [remarksBaseType] : [];
      }
      return this.dataset.types;
    },
    showTypeSelector() {
      // Madina: only Sections (questions-base) — hide type picker
      if (this.isMadinatulIlmDomain) return false;
      // HPGS / WHSS: never show Type on add/edit UI (defaults to icon-base)
      if (this.hidesTabulationTypePicker) return false;
      if (!this.isRemarksBaseSchool) return true;
      if (!this.isEdit) return false;
      return Boolean(this.loadedType && this.loadedType !== "icon-base");
    },
    currentTypeConfig() {
      if (!this.selectedType) return null;
      const config = this.dataset.types.find(
        (t) => t.typeName === this.selectedType
      );
      if (!config) return null;
      if (
        this.isMadinatulIlmDomain &&
        config.typeName === "questions-base"
      ) {
        return {
          ...config,
          displayName: "Sections",
          categories: [
            { name: "Section Name", key: "questionNumber", input: "text" },
            { name: "Marks", key: "totalMarks", input: "number" },
          ],
        };
      }
      if (this.isRemarksBaseSchool && config.typeName === "icon-base") {
        return { ...config, displayName: "Remarks Base" };
      }
      return config;
    },
    isIconBase() {
      return this.selectedType === "icon-base";
    },
    isQuestionsBase() {
      return this.selectedType === "questions-base";
    },
    questionsCountLabel() {
      return this.isMadinatulIlmSectionsMode
        ? "How many sections?"
        : "How many questions?";
    },
    questionsCountHint() {
      return this.isMadinatulIlmSectionsMode
        ? "Add sections like Oral, Written and set marks for each."
        : "Rows will auto generate based on this count.";
    },
    isSingleType() {
      return (
        this.selectedType &&
        this.currentTypeConfig &&
        !this.currentTypeConfig.multiple &&
        !this.isIconBase &&
        !this.isQuestionsBase
      );
    },
    questionsTotalMarks() {
      if (!this.isQuestionsBase) return 0;
      return this.rows.reduce((sum, row) => {
        const value = Number(row.totalMarks) || 0;
        return sum + value;
      }, 0);
    },
    dueDateConfig() {
      return {
        dateFormat: "Y-m-d",
        allowInput: true,
        altInput: true,
        altFormat: "M d, Y",
      };
    },
    canSubmit() {
      // Basic required fields check
      if (
        !this.hasClassSelection ||
        (!this.isMultiClassSelection &&
          (this.selectedSection === null ||
            this.selectedSection === undefined)) ||
        !this.selectedSubject ||
        !this.selectedType
      ) {
        return false;
      }
      if (this.rows.length === 0) {
        return false;
      }

      // Validate all rows have required fields
      return this.rows.every((row) => {
        const config = this.currentTypeConfig;
        if (!config) return false;

        // Special handling for icon-base type
        if (this.isIconBase) {
          // Check if category name is provided
          if (!row.categoryName || String(row.categoryName).trim() === "") {
            return false;
          }
          // Check if at least one option exists
          if (
            !row.options ||
            !Array.isArray(row.options) ||
            row.options.length === 0
          ) {
            return false;
          }
          // Check if all options have names
          return row.options.every((option) => {
            return (
              option &&
              option.optionName &&
              String(option.optionName).trim() !== ""
            );
          });
        }

        // For questions-base and class-test
        if (!config.categories || config.categories.length === 0) {
          return true; // If no categories defined, allow submission
        }

        return config.categories.every((cat) => {
          const value = row[cat.key];
          if (cat.input === "number") {
            // Allow 0 as valid value
            return value !== null && value !== undefined && value !== "";
          }
          if (cat.input === "icon") {
            // For icons, check if name is provided
            const iconName = row[cat.key + "_name"];
            return iconName && String(iconName).trim() !== "";
          }
          // For text fields
          return value && String(value).trim() !== "";
        });
      });
    },
  },
  async created() {
    // Initialize rights
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    // Check for rights under the parent route name (tabulation-sheet) instead of the form route name
    const parentRouteName = "tabulation-sheet";

    if (!this.$store.state.rights[parentRouteName]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[parentRouteName];

    this.isEdit = this.$route.name === "tabulation-sheet-edit";
    this.isView = this.$route.name === "tabulation-sheet-view";

    // Check rights for add/edit/view mode
    if (this.isEdit && !this.rights.edit) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    } else if (this.isView && !this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    } else if (!this.isEdit && !this.isView && !this.rights.add) {
      // Add mode
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    // Show loader immediately if editing/viewing - before any async operations
    if (this.isEdit || this.isView) {
      this.dataLoading = true;
      // Force immediate DOM update to show loader
      this.$forceUpdate();
      await this.$nextTick();
    }

    await this.loadClasses();
    await this.loadExamTypes();
    await this.loadSessions();
    if (this.isEdit || this.isView) {
      await this.loadSheetData();
    } else if (this.isMadinatulIlmDomain) {
      this.selectType("questions-base");
    } else if (this.isRemarksBaseSchool || this.isWhssDomain) {
      this.selectType("icon-base");
    }
  },
  watch: {
    selectedClassIds: {
      handler(newVal) {
        const ids = Array.isArray(newVal)
          ? newVal.map((id) => String(id).trim()).filter(Boolean)
          : [];
        if (ids.length === 0) {
          this.sections = [this.getAllSectionOption()];
          this.selectedSection = 0;
          this.subjects = [];
          this.selectedSubject = null;
          return;
        }
        if (ids.length > 1) {
          this.selectedSection = 0;
          this.sections = [this.getAllSectionOption()];
        } else {
          this.loadSectionsFromClass(ids[0]);
        }
        this.loadSubjects();
      },
      deep: true,
    },
    selectedSection(newVal) {
      // Subjects are already loaded when class is selected
      // Just clear subject selection if section changes
      if (!newVal || newVal === null || newVal === undefined) {
        this.selectedSubject = null;
      }
    },
  },
  methods: {
    ...mapActions(["get", "post", "put"]),
    getClassName(id) {
      if (id === null || id === undefined || id === "") return null;
      const idStr = String(id);
      const cls = this.classes.find((c) => String(c.id) === idStr);
      return cls ? cls.name : null;
    },
    getClassDisplayName() {
      if (!this.hasClassSelection) return null;
      return this.selectedClassIds
        .map((id) => this.getClassName(id))
        .filter(Boolean)
        .join(", ");
    },
    normalizeClassIdsFromApi(classIdRaw) {
      if (classIdRaw === null || classIdRaw === undefined || classIdRaw === "") {
        return [];
      }
      if (Array.isArray(classIdRaw)) {
        return classIdRaw
          .map((id) => String(id).trim())
          .filter((id) => id && id !== "0");
      }
      if (typeof classIdRaw === "string") {
        return classIdRaw
          .split(",")
          .map((part) => String(part).trim())
          .filter((id) => id && id !== "0");
      }
      return [String(classIdRaw).trim()].filter((id) => id && id !== "0");
    },
    formatClassIdForApi(classIds) {
      const ids = (classIds || [])
        .map((id) => String(id).trim())
        .filter((id) => id && id !== "0");
      return ids.join(",");
    },
    getSectionDisplayName() {
      if (this.isMultiClassSelection) {
        return "All";
      }
      return this.getSectionName(this.selectedSection);
    },
    getSectionName(id) {
      if (id === 0 || id === null || id === undefined) return "All";
      const sec = this.sections.find((s) => s.id === id);
      return sec ? sec.section : null;
    },
    getSubjectName(id) {
      if (!id) return null;
      const subj = this.subjects.find((s) => s.id === id);
      return subj ? subj.subject : null;
    },
    normalizeProfectusClassName(className) {
      return (className || "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, " ")
        .replace(/-+/g, "-");
    },
    isProfectusPreJuniorClassName(className) {
      const normalized = this.normalizeProfectusClassName(className);
      const compact = normalized.replace(/[\s-]+/g, "");
      return (
        normalized.startsWith("PRE JUNIOR") ||
        normalized.startsWith("PRE-JUNIOR") ||
        normalized.startsWith("PRE JR Y") ||
        normalized.startsWith("PRE-JR-Y") ||
        normalized.startsWith("PRE JRY") ||
        normalized.startsWith("PRE JR") ||
        normalized.startsWith("PRE-JR") ||
        normalized.startsWith("PREJR") ||
        compact.startsWith("PREJUNIOR") ||
        compact.startsWith("PREJR") ||
        compact.startsWith("PJ") ||
        normalized.startsWith("PJ -") ||
        normalized.startsWith("PJ-")
      );
    },
    isProfectusJuniorClassName(className) {
      const normalized = this.normalizeProfectusClassName(className);
      if (this.isProfectusPreJuniorClassName(className)) return false;
      return normalized.startsWith("JR") || normalized.startsWith("JUNIOR");
    },
    initCategoryProfectusFields(category, source = {}) {
      this.$set(category, "languagePanelOpen", source.bothRemarks === true);
    },
    toggleCategoryLanguagePanel(categoryIndex) {
      const category = this.rows[categoryIndex];
      if (!category) return;

      this.$set(category, "languagePanelOpen", !category.languagePanelOpen);
    },
    shouldInitCategoryLanguageFields(className) {
      if (this.isMyskoolDomain) return true;
      if (!this.isProfectusDomain) return false;
      return (
        this.isEdit ||
        this.isProfectusPreJuniorClassName(className || this.selectedClassName)
      );
    },
    getCategoryBothRemarks(category) {
      if (!this.usesBothRemarksPayload || !category) return null;
      return Boolean(category.languagePanelOpen);
    },
    syncProfectusCategoryFromSubject() {
      if (!this.profectusUsesSubjectAutoFill || !this.isIconBase) return;

      const subjectName = this.getSubjectName(this.selectedSubject);
      this.rows.forEach((row) => {
        row.categoryName = subjectName || "";
        row.languageCode = null;
      });
    },
    resetProfectusIconBaseRows() {
      if (!this.isRemarksBaseSchool || !this.isIconBase) return;

      if (this.rows.length === 0) {
        this.addRow();
      }
      this.syncProfectusCategoryFromSubject();
    },
    getExamTypeName(id) {
      if (!id) return null;
      const examType = this.examTypes.find((e) => e.id === id);
      return examType ? examType.examType : null;
    },
    getSessionName(id) {
      if (!id) return null;
      const session = this.sessions.find((s) => s.id === id);
      return session ? session.session : null;
    },
    async loadClasses() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        // Handle both direct array and wrapped response
        const classesData = response.data || response;
        this.classesData = Array.isArray(classesData) ? classesData : [];
        this.classes = this.classesData.map((el) => el.cls);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      }
    },
    loadSectionsFromClass(classId) {
      if (!classId) {
        this.sections = [this.getAllSectionOption()];
        this.selectedSection = 0;
        return;
      }
      const classIdStr = String(classId);
      // Find the selected class in classesData
      const selectedClassData = this.classesData.find(
        (item) => String(item.cls.id) === classIdStr
      );
      const allOption = this.getAllSectionOption();
      if (selectedClassData && selectedClassData.sections) {
        const mappedSections = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section,
          name: sec.section, // Add name for compatibility
        }));
        this.sections = [allOption, ...mappedSections];
      } else {
        this.sections = [allOption];
      }
      this.selectedSection = allOption.id;
    },
    getAllSectionOption() {
      return { id: 0, section: "All", name: "All" };
    },
    async loadSubjects() {
      // Load subjects when class is selected, independent of section selection
      if (!this.hasClassSelection) {
        this.subjects = [];
        return;
      }
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        this.subjects = await this.get(obj);
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.subjects = [];
      }
    },
    async loadSheetData() {
      // Set loader immediately - synchronously before any async operations
      this.dataLoading = true;
      // Force Vue to update DOM immediately to show loader
      this.$forceUpdate();
      await this.$nextTick();

      try {
        const obj = {
          url:
            this.$store.state.domain +
            "TabulationSheet/LoadSelected?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&id=" +
            this.$route.params.id,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data = response.data || response;
        if (data) {
          this.selectedClassIds = this.normalizeClassIdsFromApi(data.classId);
          const loadedMultiClass = this.selectedClassIds.length > 1;
          this.selectedSection = loadedMultiClass
            ? 0
            : data.sectionId !== undefined && data.sectionId !== null
              ? data.sectionId
              : 0;
          this.selectedSubject = data.subjectId;
          this.selectedExamType = data.examTypeId || null;
          this.selectedSession = data.sessionId || null;
          this.selectedType = data.type;
          this.dueDate = data.dueDate || null;
          // Store the loaded type to disable other options in edit mode
          this.loadedType = data.type;
          // Ensure the selected type exists in the dataset
          if (
            !this.dataset.types.find((t) => t.typeName === this.selectedType)
          ) {
            this.selectedType = null;
            this.rows = [];
          } else {
            // Handle different structures based on type
            if (this.selectedType === "icon-base") {
              // For icon-base: use categories array
              const incomingCategories = data.categories || [];
              this.rows = incomingCategories.map((category) => {
                const mappedRow = {
                  ...category,
                  __rowId: this.generateRowId(),
                };
                // Ensure options array exists and has proper structure
                if (!mappedRow.options || !Array.isArray(mappedRow.options)) {
                  mappedRow.options = [];
                }
                // Add __optionId to options for Vue reactivity
                mappedRow.options = mappedRow.options.map((opt) => ({
                  ...opt,
                  __optionId: this.generateRowId(),
                }));
                if (this.shouldInitCategoryLanguageFields(this.selectedClassName)) {
                  this.initCategoryProfectusFields(mappedRow, category);
                }
                return mappedRow;
              });
            } else {
              // For questions-base and class-test: use records array
              const incomingRows = data.records || [];
              this.rows = incomingRows.map((row) => {
                const mappedRow = {
                  ...row,
                  __rowId: this.generateRowId(),
                };
                // Migrate old field names for Class Test
                if (this.selectedType === "class-test") {
                  if (row.testName && !row.name) {
                    mappedRow.name = row.testName;
                  }
                  if (
                    row.totalQuestions !== undefined &&
                    row.totalQuestion === undefined
                  ) {
                    mappedRow.totalQuestion = row.totalQuestions;
                  }
                }
                return mappedRow;
              });
            }
            if (this.selectedType === "questions-base") {
              this.questionCountInput = Math.max(this.rows.length, 1);
            }
            // Only auto-fill from subject when NOT in view mode, because in view
            // mode the API already provides the correct categoryName values.
            if (!this.isView) {
              this.syncProfectusCategoryFromSubject();
            }
          }
          // Load sections and subjects after setting class/section
          if (this.hasClassSelection) {
            if (this.isMultiClassSelection) {
              this.sections = [this.getAllSectionOption()];
              this.selectedSection = 0;
            } else {
              this.loadSectionsFromClass(this.primaryClassId);
            }
            await this.loadSubjects();
          }
          // In edit mode, sync category names after subjects are loaded so the
          // subject list is populated and getSubjectName() returns correctly.
          if (!this.isView && this.isEdit) {
            this.syncProfectusCategoryFromSubject();
          }
        }
      } catch (error) {
        console.error("Error loading sheet data:", error);
        this.$bvToast.toast(
          this.isHpgsDomain
            ? "Error loading qualitative review"
            : "Error loading tabulation sheet",
          {
            title: this.$t("Error"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
        this.goBack();
      } finally {
        this.dataLoading = false;
      }
    },
    onClassChange(selectedIds) {
      this.validationErrors.class = "";
      const ids = Array.isArray(selectedIds) ? selectedIds : [];
      if (ids.length > 1) {
        this.selectedSection = 0;
      }
      // In view mode we must not auto-fill/overwrite loaded category names.
      // API response already contains the correct values.
      if (this.isView) return;
      this.resetProfectusIconBaseRows();
    },
    onSectionChange() {
      this.validationErrors.section = "";
    },
    onSubjectChange() {
      this.validationErrors.subject = "";
      // Prevent overwriting `categoryName` in view mode.
      // v-select can emit @input when we set selectedSubject programmatically.
      if (this.isView) return;
      this.syncProfectusCategoryFromSubject();
    },
    onExamTypeChange() {
      this.validationErrors.examType = "";
    },
    onSessionChange() {
      this.validationErrors.session = "";
    },
    async loadExamTypes() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "ExamTypes/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&status=active&page=1&pageSize=1000",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        // Handle both direct array and wrapped response
        const examTypesData = response.data || response;
        this.examTypes = Array.isArray(examTypesData) ? examTypesData : [];
      } catch (error) {
        console.error("Error loading exam types:", error);
        this.examTypes = [];
      }
    },
    async loadSessions() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Sessions?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        // Handle both direct array and wrapped response
        const sessionsData = response.data || response;
        this.sessions = Array.isArray(sessionsData) ? sessionsData : [];
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },
    selectType(typeName) {
      if (this.selectedType === typeName) {
        // Deselect if clicking the same type
        this.selectedType = null;
        this.rows = [];
        this.questionCountInput = 1;
      } else {
        this.selectedType = typeName;
        this.rows = [];
        this.validationErrors.type = "";
        // Always add rows by default when type is selected
        if (this.currentTypeConfig) {
          if (this.isIconBase) {
            this.initializeProfectusIconBaseRows();
          } else if (this.isQuestionsBase) {
            this.questionCountInput = Math.max(this.questionCountInput, 1);
            this.applyQuestionCount();
          } else {
            this.addRow();
          }
        }
      }
    },
    initializeProfectusIconBaseRows() {
      this.addRow();
      this.syncProfectusCategoryFromSubject();
    },
    onTypeChange() {
      this.rows = [];
      this.validationErrors.type = "";
      if (this.currentTypeConfig && !this.currentTypeConfig.multiple) {
        // For single entry types, add one row by default
        this.addRow();
      }
    },
    createEmptyRow() {
      if (!this.currentTypeConfig) return {};
      const row = {
        __rowId: this.generateRowId(),
      };
      if (this.isIconBase) {
        // For icon-base, create category structure with options array
        row.categoryName = "";
        row.languageCode = null;
        row.options = [];
        if (this.shouldInitCategoryLanguageFields()) {
          row.languagePanelOpen = false;
        }
      } else {
        this.currentTypeConfig.categories.forEach((category) => {
          if (category.input === "icon") {
            row[category.key] = false;
            row[category.key + "_name"] = ""; // Add name field for icons
          } else if (category.input === "number") {
            row[category.key] = null;
          } else {
            row[category.key] = "";
          }
        });
      }
      if (this.isQuestionsBase && !row.questionNumber) {
        // Madina Sections: leave name empty for the user to type (Oral, Written, etc.)
        row.questionNumber = this.isMadinatulIlmDomain
          ? ""
          : this.getDefaultQuestionNumber(this.rows.length + 1);
      }
      return row;
    },
    generateRowId() {
      this.rowUidCounter += 1;
      return `row-${Date.now()}-${this.rowUidCounter}`;
    },
    getDefaultQuestionNumber(index) {
      return `Q${index}`;
    },
    getQuestionRowTitle(row, rowIndex) {
      if (this.isMadinatulIlmSectionsMode) {
        return `Section ${rowIndex + 1}`;
      }
      return `Question ${rowIndex + 1}`;
    },
    getQuestionCategoryLabel(category) {
      return (category && category.name) || "";
    },
    getQuestionCategoryPlaceholder(category) {
      if (!category) return "";
      if (
        this.isMadinatulIlmSectionsMode &&
        category.key === "questionNumber"
      ) {
        return "e.g. Oral, Written";
      }
      return `Enter ${category.name}`;
    },
    applyQuestionCount() {
      if (!this.isQuestionsBase) return;
      let count = parseInt(this.questionCountInput, 10);
      if (Number.isNaN(count) || count < 1) count = 1;
      if (count > 200) count = 200;
      this.questionCountInput = count;
      const current = this.rows.length;
      if (current < count) {
        for (let i = current; i < count; i += 1) {
          const newRow = this.createEmptyRow();
          if (!newRow.questionNumber && !this.isMadinatulIlmDomain) {
            newRow.questionNumber = this.getDefaultQuestionNumber(i + 1);
          }
          this.rows.push(newRow);
        }
      } else if (current > count) {
        this.rows.splice(count);
      }
    },
    addCategory() {
      this.rows.push(this.createEmptyRow());
      this.syncProfectusCategoryFromSubject();
    },
    addOption(categoryIndex) {
      if (this.rows[categoryIndex] && this.isIconBase) {
        if (!this.rows[categoryIndex].options) {
          this.$set(this.rows[categoryIndex], "options", []);
        }
        this.rows[categoryIndex].options.push({
          __optionId: this.generateRowId(),
          optionName: "",
        });
      }
    },
    removeOption(categoryIndex, optionIndex) {
      if (this.rows[categoryIndex] && this.rows[categoryIndex].options) {
        this.rows[categoryIndex].options.splice(optionIndex, 1);
      }
    },
    addRow() {
      if (this.isIconBase) {
        this.addCategory();
      } else {
        this.rows.push(this.createEmptyRow());
        if (this.isQuestionsBase) {
          this.questionCountInput = this.rows.length;
        }
      }
    },
    removeRow(index) {
      this.rows.splice(index, 1);
      if (this.isQuestionsBase) {
        if (this.rows.length === 0) {
          this.rows.push(this.createEmptyRow());
        }
        this.questionCountInput = this.rows.length;
      }
    },
    getColumnSize(inputType) {
      if (inputType === "icon") return 12;
      return 4;
    },
    async handleSubmit() {
      // Clear previous validation errors
      this.validationErrors = {
        class: "",
        section: "",
        subject: "",
        examType: "",
        session: "",
        type: "",
      };

      // Validate
      let hasError = false;
      if (!this.hasClassSelection) {
        this.validationErrors.class = "Class is required";
        hasError = true;
      }
      if (
        !this.isMultiClassSelection &&
        (this.selectedSection === null || this.selectedSection === undefined)
      ) {
        this.validationErrors.section = "Section is required";
        hasError = true;
      }
      if (!this.selectedSubject) {
        this.validationErrors.subject = "Subject is required";
        hasError = true;
      }
      if (!this.selectedExamType) {
        this.validationErrors.examType = "Exam Type is required";
        hasError = true;
      }
      if (!this.selectedSession) {
        this.validationErrors.session = "Session is required";
        hasError = true;
      }
      if (!this.selectedType) {
        this.validationErrors.type = "Type is required";
        hasError = true;
      }
      if (this.rows.length === 0) {
        this.$bvToast.toast("Please add at least one entry", {
          title: "Validation Error",
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        hasError = true;
      }

      if (hasError) {
        return;
      }

      // Set loading state
      this.submitting = true;

      // Prepare payload according to new backend structure
      const userId =
        this.$store.state.userData.userID ||
        this.$store.state.userData.userLinkID ||
        this.$store.state.userData.localID ||
        this.$store.state.userData.id ||
        0;

      const isMultiClass = this.isMultiClassSelection;

      let output = {
        id: this.isEdit ? parseInt(this.$route.params.id) : 0,
        classId: this.formatClassIdForApi(this.selectedClassIds),
        sectionId: isMultiClass ? 0 : this.selectedSection,
        subjectId: this.selectedSubject,
        examTypeId: this.selectedExamType,
        sessionId: this.selectedSession,
        campusID: this.$store.state.userData.cId,
        type: this.selectedType,
        createdBy: userId,
        updatedBy: userId,
        isActive: true,
        records: [],
        categories: [],
        dueDate: this.dueDate || null,
      };

      if (this.isQuestionsBase) {
        // For questions-base: use records array
        output.records = this.rows.map((row, index) => {
          const { __rowId, ...rest } = row;
          return {
            id: rest.id || 0, // 0 for new, existing ID for edit
            questionNumber: rest.questionNumber || "",
            totalMarks: rest.totalMarks || 0,
            sortIndex: index + 1,
            deleted: false,
          };
        });
      } else if (this.isIconBase) {
        // For icon-base: use categories array instead of records
        output.categories = this.rows.map((row, index) => {
          const { __rowId, languagePanelOpen, ...rest } = row;
          const bothRemarks = this.getCategoryBothRemarks(row);
          return {
            id: rest.id || 0, // 0 for new, existing ID for edit
            categoryName: rest.categoryName || "",
            languageCode: rest.languageCode || null,
            bothRemarks: this.usesBothRemarksPayload ? bothRemarks : null,
            sortIndex: index + 1,
            deleted: false,
            options: (rest.options || []).map((option, optIndex) => {
              const { __optionId, ...optionRest } = option;
              return {
                id: optionRest.id || 0, // 0 for new, existing ID for edit
                optionName: optionRest.optionName || "",
                bothRemarks: this.usesBothRemarksPayload ? bothRemarks : null,
                sortIndex: optIndex + 1,
                deleted: false,
                options: null,
              };
            }),
          };
        });
      } else if (this.selectedType === "class-test") {
        // For class-test: use records array
        output.records = this.rows.map((row, index) => {
          const { __rowId, ...rest } = row;
          return {
            id: rest.id || 0, // 0 for new, existing ID for edit
            name: rest.name || "",
            totalQuestion: rest.totalQuestion || 0,
            totalMarks: rest.totalMarks || 0,
            sortIndex: index + 1,
            deleted: false,
          };
        });
      }

      // Log payload for debugging
      console.log("Submitting payload:", output);

      try {
        // Use POST for both create and update (as per new API structure)
        const response = await this.post({
          url:
            this.$store.state.domain +
            "TabulationSheet/Save?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            userId,
          body: output,
          token: this.$store.state.userData.token,
          context: this,
          message: this.isEdit
            ? this.isHpgsDomain
              ? "Qualitative review updated successfully!"
              : "Tabulation sheet updated successfully!"
            : this.isHpgsDomain
              ? "Qualitative review created successfully!"
              : "Tabulation sheet created successfully!",
        });
        if (response) {
          this.goBack();
        }
      } catch (error) {
        console.error("Error submitting tabulation sheet:", error);
        this.$bvToast.toast(
          (this.isHpgsDomain
            ? "Error saving qualitative review: "
            : "Error saving tabulation sheet: ") +
            (error.message || "Unknown error"),
          {
            title: this.$t("Error"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      this.$router.push({ name: "tabulation-sheet" });
    },
  },
};
</script>

<style lang="scss" scoped>
.tabulation-sheet-form {
  .card {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  }

  // Form Content Section
  .form-content-section {
    margin-top: 1.5rem;
  }

  .form-section-header {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    padding: 1rem 1.25rem;
    border-radius: 8px 8px 0 0;
    margin-bottom: 0;

    .section-title {
      color: white;
      font-size: 1.1rem;
      font-weight: 600;
      margin: 0;
    }

    .add-row-btn,
    .add-category-header-btn {
      background: white;
      border: none;
      color: #8b5cf6;
      width: 36px;
      height: 36px;
      padding: 0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

      &:hover {
        background: #f3f0ff;
        color: #7c3aed;
        transform: scale(1.1) rotate(90deg);
        box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
      }

      &:active {
        transform: scale(0.95);
      }

      .feather-icon {
        color: inherit;
      }
    }
  }

  .form-rows-container {
    background: white;
    border: 1px solid #e3e6f0;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    min-height: 200px;
    overflow: visible;
  }

  .icon-base-header {
    background: linear-gradient(135deg, #8055e0 0%, #6930c3 100%);
  }

  .icon-base-container {
    min-height: auto;
  }

  .question-base-container {
    min-height: auto;
  }

  .icon-base-categories {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .profectus-prejr-card {
    &:hover {
      transform: none;
    }

    .category-header--prejr {
      flex-wrap: nowrap;
      align-items: center;
      margin-bottom: 1rem;
    }
  }

  .profectus-lang-expand-btn {
    border-radius: 8px;
    width: 38px;
    height: 38px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-color: #d8d6de;

    &:hover,
    &:focus {
      background: #f3f0ff;
      border-color: #8b5cf6;
      box-shadow: none;
    }
  }

  .profectus-lang-inline-group {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .profectus-lang-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    height: 38px;
    padding: 0 0.75rem;
    border: 1.5px solid #d8d6de;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    user-select: none;
    outline: none;
    line-height: 1;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &.inactive {
      background: #f3f4f6;
      border-color: #e5e7eb;

      .profectus-lang-box-code,
      .profectus-lang-box-label {
        color: #9ca3af;
      }

      &:hover:not(:disabled) {
        border-color: #d1d5db;
        background: #eceef2;
      }
    }

    &.active {
      border-color: #7c5dfa;
      background: #7c5dfa;

      .profectus-lang-box-code,
      .profectus-lang-box-label {
        color: #fff;
      }

      &:hover:not(:disabled) {
        border-color: #6d4ed9;
        background: #6d4ed9;
      }
    }

    &.is-display-only {
      cursor: default;
      pointer-events: none;
    }

    &.is-readonly,
    &:disabled {
      cursor: default;
      opacity: 1;
    }

    &:disabled.inactive {
      opacity: 0.75;
      cursor: not-allowed;
    }
  }

  .profectus-lang-box-code {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #7c5dfa;
    line-height: 1;
  }

  .profectus-lang-box-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
    line-height: 1;
    white-space: nowrap;
  }

  .category-card {
    background: #fefefe;
    border: 1px solid #e0def5;
    border-radius: 10px;
    padding: 1.25rem;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
    transition: all 0.3s ease;

    &:hover {
      border-color: #8b5cf6;
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
      transform: translateY(-1px);
    }
  }

  .category-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #eceef5;
  }

  .category-name-input {
    flex: 1;
    border: 1px solid #d8d6de;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    font-weight: 500;
    color: #4b4b63;
    font-size: 0.95rem;
    transition: all 0.2s ease;

    &:focus {
      border-color: #8b5cf6;
      box-shadow: 0 0 0 0.2rem rgba(139, 92, 246, 0.25);
    }

    &--auto:disabled {
      background-color: #f8f9fa;
      cursor: not-allowed;
    }
  }

  .remove-category-btn {
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3);
    }
  }

  .category-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .options-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .options-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #5a5c69;
  }

  .add-option-btn {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    font-weight: 500;
    border-radius: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .options-draggable {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .option-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem;
    background: #fafbfc;
    border: 1px solid #e3e6f0;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: #f3f0ff;
      border-color: #d8d6de;
      box-shadow: 0 2px 6px rgba(139, 92, 246, 0.1);
    }
  }

  .option-drag-handle {
    width: 26px;
    height: 26px;
    border: 1px dashed #c7c9da;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: grab;
    transition: border-color 0.2s ease, background 0.2s ease;
    flex-shrink: 0;

    .handle-dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: #6c6f85;
      margin: 1px 0;
    }

    &:hover {
      border-color: #8b5cf6;
      background: #f3f0ff;
    }

    &:active {
      cursor: grabbing;
    }
  }

  .option-input {
    flex: 1;
    border: 1px solid #d8d6de;
    border-radius: 6px;
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    transition: all 0.2s ease;

    &:focus {
      border-color: #8b5cf6;
      box-shadow: 0 0 0 0.15rem rgba(139, 92, 246, 0.2);
    }
  }

  .remove-option-btn {
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3);
    }
  }

  .no-options-message {
    padding: 1.25rem;
    text-align: center;
    color: #6c757d;
    font-size: 0.85rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px dashed #dee2e6;
  }

  .question-config-panel {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    background: #fafbff;
    border: 1px solid #ebe7ff;
    border-radius: 10px;
    margin-bottom: 1rem;
  }

  .question-count-block {
    flex: 1 1 280px;
  }

  .question-count-label {
    font-weight: 600;
    color: #5a5c69;
  }

  .question-count-input {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.35rem;

    .form-control {
      max-width: 140px;
    }
  }

  .question-total-card {
    min-width: 180px;
    padding: 0.85rem 1.25rem;
    background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
    border-radius: 10px;
    color: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;

    .label {
      font-size: 0.85rem;
      opacity: 0.9;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 600;
    }
  }

  .question-draggable {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .question-row-card {
    background: #fefefe;
    border: 1px solid #e0def5;
    border-radius: 10px;
    padding: 1rem;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  }

  .question-row-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
    border-bottom: 1px solid #eceef5;
    padding-bottom: 0.5rem;
  }

  .question-row-title {
    font-weight: 600;
    color: #4b4b63;
  }

  .question-row-marks {
    font-size: 0.85rem;
    color: #6c757d;
  }

  .question-drag-handle {
    width: 26px;
    height: 26px;
    border: 1px dashed #c7c9da;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: grab;
    transition: border-color 0.2s ease, background 0.2s ease;

    .handle-dot {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: #6c6f85;
      margin: 1px 0;
    }

    &:hover {
      border-color: #8b5cf6;
      background: #f3f0ff;
    }
  }

  .question-row-card .remove-row-btn {
    margin-left: 0.5rem;
  }

  .single-type-form {
    background: #fefefe;
    border: 1px solid #e0def5;
    border-radius: 10px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .form-fields-row {
      margin: 0;
    }

    .form-field-col {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
      margin-bottom: 1rem;
    }

    .form-group {
      margin-bottom: 0;

      label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #5a5c69;
        margin-bottom: 0.5rem;
      }

      .form-control {
        border: 1px solid #d8d6de;
        border-radius: 6px;
        transition: all 0.2s ease;

        &:focus {
          border-color: #8b5cf6;
          box-shadow: 0 0 0 0.2rem rgba(139, 92, 246, 0.25);
        }
      }
    }
  }

  .form-row-card {
    background: #fafbfc;
    border: 1px solid #e3e6f0;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1.25rem;
    transition: all 0.3s ease;

    &:hover {
      border-color: #8b5cf6;
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
      transform: translateY(-1px);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .row-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;

    .row-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #5a5c69;
    }

    .remove-row-btn {
      width: 28px;
      height: 28px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;

      &:hover {
        transform: scale(1.1);
        box-shadow: 0 2px 6px rgba(220, 53, 69, 0.3);
      }
    }
  }

  .form-fields-row {
    margin: 0 -0.5rem;
  }

  .form-field-col {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    margin-bottom: 1rem;
  }

  .form-group {
    margin-bottom: 0;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #5a5c69;
      margin-bottom: 0.5rem;
      display: block;
    }

    .form-control {
      border: 1px solid #d8d6de;
      border-radius: 6px;
      padding: 0.625rem 0.875rem;
      font-size: 0.875rem;
      transition: all 0.2s ease;
      width: 100%;

      &:focus {
        border-color: #8b5cf6;
        box-shadow: 0 0 0 0.2rem rgba(139, 92, 246, 0.25);
      }
    }
  }

  .icon-input-wrapper {
    display: flex;
    align-items: center;
    gap: 1rem;

    .icon-name-input {
      flex: 1;
      min-width: 0;
    }

    .icon-toggle {
      flex-shrink: 0;
      margin: 0;
      white-space: nowrap;

      .toggle-label-active {
        color: #8b5cf6;
        font-weight: 500;
      }

      .toggle-label-inactive {
        color: #6c757d;
      }
    }
  }

  // Type selector improvements
  .type-selector {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e3e6f0;
  }

  .type-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border: 2px solid #e3e6f0;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    min-width: 140px;

    &:hover:not(.disabled) {
      border-color: #8b5cf6;
      background: #f3f0ff;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(139, 92, 246, 0.15);
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
      background: #f8f9fa;

      &:hover {
        border-color: #e3e6f0;
        background: #f8f9fa;
        transform: none;
        box-shadow: none;
      }

      .type-icon {
        color: #9ca3af;
      }

      .type-label {
        color: #9ca3af;
      }
    }

    .type-disabled-badge {
      font-size: 0.75rem;
      color: #6c757d;
      font-style: italic;
      margin-left: auto;
    }

    &.active {
      border-color: #8b5cf6;
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
      color: white;
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);

      .type-icon {
        color: white;
      }

      .type-label {
        color: white;
        font-weight: 600;
      }
    }
  }

  .type-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(139, 92, 246, 0.1);
    transition: all 0.2s ease;
  }

  .type-item.active .type-icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
  }

  .type-icon {
    color: #8b5cf6;
    transition: all 0.2s ease;
  }

  .type-label {
    font-weight: 500;
    color: #5a5c69;
    font-size: 0.875rem;
    transition: all 0.2s ease;
  }

  // Save and Cancel buttons styling
  .save-btn {
    padding: 0.75rem 2rem;
    font-weight: 600;
    font-size: 0.95rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(40, 199, 111, 0.3);
    transition: all 0.3s ease;
    min-width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(40, 199, 111, 0.4);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .feather-icon {
      margin-right: 0.5rem;
    }
  }

  .cancel-btn {
    padding: 0.75rem 2rem;
    font-weight: 600;
    font-size: 0.95rem;
    border-radius: 8px;
    transition: all 0.3s ease;
    min-width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #6c757d;

    &:hover {
      background-color: #f8f9fa;
      border-color: #5a6268;
      transform: translateY(-1px);
    }

    .feather-icon {
      margin-right: 0.5rem;
    }
  }

  // View Info Section Styles
  .view-info-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .view-info-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .view-info-label {
    font-weight: 600;
    color: #5a5c69;
    font-size: 0.9375rem;
    min-width: 100px;
  }

  .view-info-value {
    font-size: 0.9375rem;
    padding: 0.5rem 1rem;
    font-weight: 500;
  }

  .view-type-badge {
    font-size: 1.125rem;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
  }

  // View Mode Styles - Classic Display
  .view-mode-container {
    background: #ffffff;
    border-radius: 8px;
    padding: 2rem;
    margin-top: 1.5rem;
  }

  .view-header-section {
    border-bottom: 2px solid #e3e6f0;
    padding-bottom: 1rem;
    margin-bottom: 2rem;
  }

  .view-section-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    letter-spacing: 0.5px;
  }

  // Icon Base View Styles
  .view-icon-base-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
  }

  .view-category-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border: 2px solid #e3e6f0;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &:hover {
      border-color: #8b5cf6;
      box-shadow: 0 4px 16px rgba(139, 92, 246, 0.15);
      transform: translateY(-2px);
    }
  }

  .view-category-header {
    border-bottom: 2px solid #8b5cf6;
    padding-bottom: 0.75rem;
    margin-bottom: 1rem;
  }

  .view-category-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .view-options-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .view-option-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #ffffff;
    border-left: 4px solid #8b5cf6;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;

    &:hover {
      background: #f3f0ff;
      transform: translateX(4px);
    }
  }

  .view-option-number {
    font-weight: 700;
    color: #8b5cf6;
    min-width: 24px;
    font-size: 1rem;
  }

  .view-option-name {
    font-size: 1rem;
    color: #2c3e50;
    font-weight: 500;
    flex: 1;
  }

  // Questions Base View Styles
  .view-questions-container {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
  }

  .view-questions-header {
    display: flex;
    justify-content: flex-end;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
    border-bottom: 2px solid #e3e6f0;
  }

  .view-total-marks {
    font-size: 1.125rem;
    color: #2c3e50;
    font-weight: 600;

    strong {
      color: #8b5cf6;
      font-size: 1.25rem;
    }
  }

  .view-questions-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .view-question-item {
    background: #ffffff;
    border: 1px solid #e3e6f0;
    border-radius: 8px;
    padding: 1.25rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .view-question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .view-question-number {
    font-size: 1.125rem;
    font-weight: 700;
    color: #2c3e50;
  }

  .view-question-marks {
    font-size: 1rem;
    font-weight: 600;
    color: #8b5cf6;
    background: #f3f0ff;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
  }

  // Class Test View Styles
  .view-class-test-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .view-class-test-item {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 2px solid #e3e6f0;
    border-radius: 10px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &:hover {
      border-color: #8b5cf6;
      box-shadow: 0 4px 16px rgba(139, 92, 246, 0.15);
      transform: translateY(-2px);
    }
  }

  .view-class-test-header {
    border-bottom: 2px solid #8b5cf6;
    padding-bottom: 0.75rem;
    margin-bottom: 1rem;
  }

  .view-test-name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2c3e50;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .view-class-test-details {
    display: flex;
    gap: 1.5rem;
  }

  .view-test-detail {
    font-size: 0.9375rem;
    color: #5a5c69;

    strong {
      color: #8b5cf6;
      font-size: 1.125rem;
      margin-left: 0.25rem;
    }
  }

  // Other Types View Styles
  .view-other-container {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .view-other-item {
    background: #ffffff;
    border: 1px solid #e3e6f0;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .view-other-field {
    display: flex;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .view-field-label {
    font-weight: 600;
    color: #5a5c69;
    min-width: 150px;
    font-size: 0.9375rem;
  }

  .view-field-value {
    color: #2c3e50;
  }

  // Skeleton Loading Styles for View Mode
  .view-skeleton-container {
    padding: 0;
  }

  .view-header-card-skeleton {
    margin-bottom: 1.5rem;
    background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
    border: 1px solid #e0d8ff;
    border-radius: 20px;
    padding: 1.75rem;
  }

  .skeleton-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
    border-bottom: 2px solid #f0edff;
  }

  .skeleton-title {
    height: 28px;
    width: 250px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeletonPulse 1.5s ease-in-out infinite;
    border-radius: 8px;
  }

  .skeleton-badge {
    height: 24px;
    width: 100px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeletonPulse 1.5s ease-in-out infinite;
    border-radius: 12px;
  }

  .skeleton-meta {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }

  .skeleton-meta-item {
    height: 80px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeletonPulse 1.5s ease-in-out infinite;
    border-radius: 14px;
  }

  .view-content-skeleton {
    background: #ffffff;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e0d8ff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }

  .skeleton-content-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .skeleton-content-item {
    flex: 1;
    height: 120px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeletonPulse 1.5s ease-in-out infinite;
    border-radius: 12px;
  }

  /* Due Date and Locked Badges */
  .due-date-badge {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
    color: #0369a1;
    border: 1px solid #7dd3fc;
    transition: all 0.2s ease;

    svg {
      color: #0284c7;
    }
  }

  .locked-badge {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #991b1b;
    border: 1px solid #fca5a5;
    transition: all 0.2s ease;
    animation: pulse-subtle 2s ease-in-out infinite;

    svg {
      color: #dc2626;
    }
  }

  @keyframes pulse-subtle {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.95;
      transform: scale(0.98);
    }
  }

  @keyframes skeletonPulse {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  // Empty Message
  .view-empty-message {
    text-align: center;
    padding: 3rem 1rem;
    color: #6c757d;
    font-size: 1rem;
    font-style: italic;
    background: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #dee2e6;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
